import React, { useEffect, useMemo, useState } from 'react';
import useProcedureStore from '../../stores/procedureStore';
import { excludeLabTests } from '../../constants/procedures';
import useOrgSettingsStore from '../../stores/orgSettingsStore';
import usePatientPlanStore from '../../stores/patientPlanStore';
import useDepartmentStore from '../../stores/departmentStore';
import { filterProvidersByDept, procedurePickerDeptAnchor } from '../visit/departmentResolution';
import { formatStaffName } from '../../utils/staffName';
import ToothNumberInput from './widget/dental/ToothNumberInput';
import { defaultDentition } from './widget/dental/dentition';
import ValidityControl, { plusDaysISO } from '../common/ValidityControl';

/**
 * AddProcedureModal — doctor/nurse/receptionist adds a procedure to a visit.
 *
 * Phase 1 design simplification: the 4-step wizard from the plan doc is
 * collapsed into a single scrollable form grouped by section (pick → estimate
 * → timing → assignee) so the first version ships quickly. We can split into
 * real steps in Phase 2 alongside the Plan Builder.
 *
 * Props:
 *   open               — bool
 *   onClose            — () => void
 *   visit              — the active Visit row ({ visit_id, patient_id, doctor_member_id, ... })
 *   viewer             — { memberId, role }
 *   doctors            — optional array for the assignee picker; falls back to [viewer]
 *   nurses             — optional array for the assignee picker
 *   onCreated(result)  — async, called with the `createPlan` result on success
 *
 * Notes:
 *   - The catalog is loaded via useProcedureStore (filtered per viewer's role
 *     flags: nurse-initiated requires nurse_allowed, receptionist requires
 *     receptionist_allowed; doctors see all active rows).
 *   - `estimated_cost` defaults to the catalog's default_cost; doctor can
 *     override. Discount field only shown for DOCTOR / ADMIN.
 *   - Timing picker: "Now" (default) | "Later today" | "Specific date" —
 *     the first two leave scheduled_for null; the third sends an ISO datetime.
 */

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

function AddProcedureModal({
  open,
  onClose,
  visit,
  viewer,
  doctors = [],
  nurses = [],
  onCreated,
  /**
   * Phase 3.1 — when set, the modal opens with these tooth tokens
   * pre-filled in the tooth-number step (e.g. "click tooth #16 on
   * the chart → modal opens with [16] pre-selected").
   */
  presetToothNumbers = null,
  /**
   * Phase 3.1 — restrict the catalog to this category. Used for the
   * dental-context entry where we don't want to surface non-dental
   * procedures.
   */
  categoryFilter = null,
}) {
  const role = (viewer?.role || '').toUpperCase();
  const canDiscount = role === 'DOCTOR' || role === 'ADMIN';

  // Catalog load — A4/S3: the add-procedure picker reads the SERVER-scoped
  // slice (dept-tagged ∪ org-wide) for THIS visit's department, NOT the admin
  // catalog. Anchor = visit.department_id; falsy/EMERGENCY → null → full list.
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const pickerDeptAnchor = procedurePickerDeptAnchor(visit);
  const { createPlan, isCreating, error: createError } =
    usePatientPlanStore();

  // Form state
  const [procedureId, setProcedureId] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [discountReason, setDiscountReason] = useState('');
  // W2/T3 — quantity (int >=1, default 1). Estimate/discount above are PER
  // UNIT; the server computes final_cost = quantity × unit_cost − discount
  // (POST /organisation/plans frozen contract) — client totals are display-only.
  const [quantity, setQuantity] = useState(1);
  const [timingMode, setTimingMode] = useState('now'); // 'now' | 'later_today' | 'scheduled'
  const [scheduledDate, setScheduledDate] = useState(TODAY_ISO());
  const [assigneeMemberId, setAssigneeMemberId] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);
  // Expiry window — "Valid until" date (default advised + 30d) + standing toggle.
  const [expiresAt, setExpiresAt] = useState(plusDaysISO(30));
  const [isStanding, setIsStanding] = useState(false);
  const [rememberPrefs, setRememberPrefs] = useState(false);
  const [paymentTiming, setPaymentTiming] = useState(''); // '' = inherit from catalog
  const [formError, setFormError] = useState('');
  // Phase 1c — senior's share-with-assignee flags. Default all-true;
  // only rendered when the assignee is someone other than the viewer.
  const [shareVitals, setShareVitals] = useState(true);
  const [shareSymptoms, setShareSymptoms] = useState(true);
  const [shareNotes, setShareNotes] = useState(true);
  const [sharePrescription, setSharePrescription] = useState(true);
  // Phase 3.1 — tooth-number tokens for is_tooth_specific procedures.
  const [toothNumbers, setToothNumbers] = useState([]);

  // Scoped catalog fetch — separate effect keyed ONLY on the (primitive) dept
  // anchor so it can't storm on unrelated re-renders. The store fetch is
  // single-flight/idempotent, so a repeat open for the same dept is a no-op.
  useEffect(() => {
    if (open) fetchProceduresForDept(pickerDeptAnchor);
  }, [open, pickerDeptAnchor, fetchProceduresForDept]);

  useEffect(() => {
    if (open) {
      // Reset form every time we re-open (fresh slate per add)
      setProcedureId('');
      setEstimatedCost('');
      setDiscountAmount('0');
      setDiscountReason('');
      setQuantity(1);
      setTimingMode('now');
      setScheduledDate(TODAY_ISO());
      setAssigneeMemberId(viewer?.memberId || '');
      setRequiresApproval(false);
      setExpiresAt(plusDaysISO(30));
      setIsStanding(false);
      setRememberPrefs(false);
      setPaymentTiming('');
      setFormError('');
      setShareVitals(true);
      setShareSymptoms(true);
      setShareNotes(true);
      setSharePrescription(true);
      // Phase 3.1 — pre-fill tooth numbers from the caller (e.g. tooth-
      // click on the odontogram pre-scopes the modal to that FDI).
      setToothNumbers(Array.isArray(presetToothNumbers) ? [...presetToothNumbers] : []);
    }
  }, [open, viewer?.memberId, presetToothNumbers]);

  // Role-filtered catalog (+ Phase 3.1 categoryFilter). Sourced from the
  // dept-scoped picker slice (A4/S3), then narrowed by role/category as before.
  const visibleProcedures = useMemo(() => {
    return excludeLabTests(pickerProcedures)
      .filter((p) => p.active !== false)
      .filter((p) => {
        if (role === 'DOCTOR' || role === 'ADMIN') return true;
        if (role === 'NURSE') return p.nurse_allowed;
        if (role === 'RECEPTIONIST') return p.receptionist_allowed;
        return false;
      })
      .filter((p) => !categoryFilter || p.category === categoryFilter);
  }, [pickerProcedures, role, categoryFilter]);

  const selected = useMemo(
    () => visibleProcedures.find((p) => p.procedure_id === procedureId) || null,
    [visibleProcedures, procedureId],
  );

  // Skip-payment policy: clinic-wide master OR this procedure's own flag.
  // When effective, nothing is charged and the estimate is locked at ₹0.
  const skipPaymentAll = useOrgSettingsStore((s) => s.skipPaymentAll);
  const fetchOrgSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  useEffect(() => { fetchOrgSettings(); }, [fetchOrgSettings]);
  const effectiveSkip = skipPaymentAll || !!selected?.skip_payment;

  // When the doctor picks a procedure, seed the estimate from its default_cost
  // and preload requires_approval_default.
  useEffect(() => {
    if (selected) {
      if (!estimatedCost) setEstimatedCost(String(selected.default_cost ?? ''));
      if (selected.requires_approval_default) setRequiresApproval(true);
      if (selected.payment_timing && !paymentTiming) setPaymentTiming(selected.payment_timing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedureId]);

  // A2 completion — dept-scope the "Assign to" candidate list to the VISIT'S
  // stamped department of care (frozen at registration), the SAME rule the
  // already-migrated widget picker (AssigneePicker/selectAssigneeCandidates) and
  // registration use. This modal is the un-migrated twin — it builds labeled
  // options inline — so we converge it onto the shared `filterProvidersByDept`
  // (single source; keeps no-dept staff org-wide, excludes other-dept, fail-opens
  // on un-hydrated edges). Anchor = visit.department_id; falsy → no filter (org
  // with 0 depts, or a no/emergency-dept visit) — full role list, as before.
  // Display-only; the server re-validates on submit.
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);
  const visitDepartmentId = visit?.department_id || null;
  useEffect(() => {
    if (open && visitDepartmentId) hydrateAllMemberDepts?.();
  }, [open, visitDepartmentId, hydrateAllMemberDepts]);

  const scopedDoctors = useMemo(
    () => filterProvidersByDept(doctors, visitDepartmentId, deptsByMember),
    [doctors, visitDepartmentId, deptsByMember],
  );
  const scopedNurses = useMemo(
    () => filterProvidersByDept(nurses, visitDepartmentId, deptsByMember),
    [nurses, visitDepartmentId, deptsByMember],
  );

  const assigneeOptions = useMemo(() => {
    const items = [];
    if (viewer?.memberId) {
      items.push({
        member_id: viewer.memberId,
        label: 'Myself',
        role: viewer.role,
      });
    }
    for (const d of scopedDoctors) {
      if (d.member_id !== viewer?.memberId) {
        const name = formatStaffName(d) || d.member_id;
        const spec = (d.specialization || '').trim();
        items.push({
          member_id: d.member_id,
          label: spec ? `Dr. ${name} · ${spec}` : `Dr. ${name}`,
          role: 'DOCTOR',
        });
      }
    }
    for (const n of scopedNurses) {
      const name = formatStaffName(n) || n.member_id;
      items.push({
        member_id: n.member_id,
        label: `Nurse · ${name}`,
        role: 'NURSE',
      });
    }
    return items;
  }, [viewer, scopedDoctors, scopedNurses]);

  // W2/T3 — qty stepper (RegistrationProceduresSection pattern): default 1,
  // min 1, integer only. `bumpQty` (+/- buttons) always lands on a valid
  // integer by construction; `setQty` (typed input) rejects 0/blank/negative/
  // non-numeric by falling back to the current quantity.
  const bumpQty = (delta) => setQuantity((q) => Math.max(1, q + delta));
  const setQty = (raw) => {
    const n = parseInt(raw, 10);
    setQuantity((prev) => (Number.isInteger(n) && n >= 1 ? n : prev));
  };

  const validate = () => {
    if (!procedureId) return 'Pick a procedure.';
    if (timingMode === 'scheduled' && !scheduledDate) return 'Pick a date.';
    const d = Number(discountAmount || 0);
    if (d > 0 && (!discountReason || discountReason.trim().length < 3)) {
      return 'Provide a short discount reason (min 3 chars).';
    }
    const est = Number(estimatedCost || 0);
    if (est < 0) return 'Estimate cannot be negative.';
    // Discount is a flat amount subtracted AFTER qty × unit (server:
    // final_cost = quantity × unit_cost − discount) — compare against the total.
    if (d > est * quantity) return 'Discount cannot exceed total estimate.';
    // Phase 3.1 — tooth-specific procedures require at least one token.
    if (selected?.is_tooth_specific && (!toothNumbers || toothNumbers.length === 0)) {
      return 'Pick at least one tooth (FDI number, quadrant, or FM).';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setFormError(err);
      return;
    }
    setFormError('');

    const isDelegated = assigneeMemberId && assigneeMemberId !== viewer?.memberId;
    const body = {
      patient_id: visit.patient_id,
      source_visit_id: visit.visit_id,
      procedure_id: procedureId,
      estimated_cost: String(estimatedCost || 0),
      discount_amount: String(discountAmount || 0),
      discount_reason: Number(discountAmount || 0) > 0 ? discountReason.trim() : null,
      quantity,
      timing_mode: timingMode,
      scheduled_for:
        timingMode === 'scheduled'
          ? `${scheduledDate}T00:00:00`
          : null,
      assigned_to_member_id: assigneeMemberId || null,
      requires_approval: requiresApproval,
      // Expiry window — "Valid until" (or review date when standing).
      expires_at: expiresAt || null,
      standing: isStanding,
      remember_preferences: rememberPrefs,
      payment_timing: paymentTiming || null,
      // Phase 1c — only send share_context when delegating to someone else.
      // Self-assigned procedures have no "handoff," so flags don't apply.
      shared_context: isDelegated
        ? {
            include_vitals: shareVitals,
            include_symptoms: shareSymptoms,
            include_notes: shareNotes,
            include_prescription: sharePrescription,
          }
        : null,
      // Phase 3.1 — only send tooth_numbers when the catalog row needs them.
      tooth_numbers: selected?.is_tooth_specific && toothNumbers?.length
        ? toothNumbers
        : null,
    };

    const result = await createPlan(body);
    if (result.success) {
      if (onCreated) await onCreated(result);
      onClose?.();
    } else {
      setFormError(result.error || 'Could not add procedure.');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add procedure</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-lg leading-none px-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* 1. Procedure pick */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Procedure *</label>
          <select
            value={procedureId}
            onChange={(e) => setProcedureId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            required
          >
            <option value="">— choose —</option>
            {visibleProcedures.map((p) => (
              <option key={p.procedure_id} value={p.procedure_id}>
                {p.name} · ₹{p.default_cost} · {p.category}
              </option>
            ))}
          </select>
          {visibleProcedures.length === 0 && (
            <p className="text-xs text-slate-500 mt-1">
              No procedures available for your role.
            </p>
          )}
        </div>

        {/* Phase 3.1 — tooth-number step (only when picked row needs it) */}
        {selected?.is_tooth_specific && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Tooth (FDI / quadrant / FM) *
            </label>
            <ToothNumberInput
              value={toothNumbers}
              onChange={setToothNumbers}
              dentitionMode={defaultDentition(visit?.patient_age)}
            />
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
              Type 16, 17 etc. — or use Q1–Q4 / FM for quadrant or full-mouth.
            </p>
          </div>
        )}

        {/* 2. Estimate + discount */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Estimate (₹) *</label>
            <input
              type="number"
              min="0"
              step="1"
              value={effectiveSkip ? 0 : estimatedCost}
              onChange={(e) => setEstimatedCost(e.target.value)}
              disabled={effectiveSkip}
              title={effectiveSkip
                ? 'No charge — payment is skipped for this procedure'
                : undefined}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
            />
            {selected && !effectiveSkip && (
              <p className="text-xs text-slate-500 mt-1">
                Default: ₹{selected.default_cost}
              </p>
            )}
            {effectiveSkip && (
              <p className="text-xs text-teal-700 dark:text-teal-300 mt-1">
                ✓ No payment needed — goes straight to the assignee's queue.
              </p>
            )}
          </div>
          {canDiscount && !effectiveSkip && (
            <div>
              <label className="block text-sm font-medium mb-1">Discount (₹)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              />
            </div>
          )}
        </div>

        {/* W2/T3 — Quantity stepper. Estimate above is PER UNIT; total shown
            here is display-only (server computes final_cost = qty × unit −
            discount and ignores any client total). */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Quantity</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => bumpQty(-1)}
              disabled={quantity <= 1}
              aria-label="Decrease quantity"
              className="w-7 h-7 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 disabled:opacity-60"
            >
              −
            </button>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={quantity}
              aria-label="Quantity"
              onChange={(e) => setQty(e.target.value)}
              className="w-16 text-center px-2 py-1 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
            <button
              type="button"
              onClick={() => bumpQty(1)}
              aria-label="Increase quantity"
              className="w-7 h-7 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
            >
              +
            </button>
            {!effectiveSkip && (
              <span className="text-sm font-semibold ml-1">
                Total: ₹{(Number(estimatedCost || 0) * quantity).toLocaleString('en-IN')}
              </span>
            )}
          </div>
        </div>

        {canDiscount && Number(discountAmount) > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Discount reason *</label>
            <input
              type="text"
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              placeholder="e.g. Senior citizen concession"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              minLength={3}
            />
          </div>
        )}

        {/* 3. Timing */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">When</label>
          <div className="flex gap-2 flex-wrap">
            {[
              ['now', 'Now'],
              ['later_today', 'Later today'],
              ['scheduled', 'Specific date'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTimingMode(key)}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  timingMode === key
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white dark:bg-slate-800 border-slate-300 text-slate-700 dark:text-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {timingMode === 'scheduled' && (
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              min={TODAY_ISO()}
              className="mt-2 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
          )}
          <div className="mt-3">
            <ValidityControl
              expiresAt={expiresAt}
              standing={isStanding}
              oneTimeOnly={!!selected?.one_time_only}
              oneTimeOnlyReason={selected?.one_time_only_reason}
              onChange={({ expiresAt: ea, standing }) => {
                setExpiresAt(ea);
                setIsStanding(standing);
              }}
            />
          </div>
        </div>

        {/* 4. Payment timing + assignee + approval */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Payment</label>
            <select
              value={paymentTiming}
              onChange={(e) => setPaymentTiming(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            >
              <option value="">Use catalog default</option>
              <option value="before">Before procedure</option>
              <option value="after">After procedure</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Assign to</label>
            <select
              value={assigneeMemberId}
              onChange={(e) => setAssigneeMemberId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            >
              {assigneeOptions.map((o) => (
                <option key={o.member_id} value={o.member_id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={requiresApproval}
              onChange={(e) => setRequiresApproval(e.target.checked)}
            />
            Needs my approval on complete
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={rememberPrefs}
              onChange={(e) => setRememberPrefs(e.target.checked)}
            />
            Remember for next time
          </label>
        </div>

        {/* Phase 1c — share-with-assignee flags. Only relevant when the
            sitting is handed off to someone else. Default all checked. */}
        {assigneeMemberId && assigneeMemberId !== viewer?.memberId && (
          <div className="mb-4 border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50 dark:bg-slate-800/40">
            <div className="text-sm font-medium mb-1">Share with assignee</div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
              Pick what clinical context the assignee can see on this visit.
              Procedure notes always flow both ways.
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={shareVitals}
                  onChange={(e) => setShareVitals(e.target.checked)}
                />
                Vitals
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={shareSymptoms}
                  onChange={(e) => setShareSymptoms(e.target.checked)}
                />
                Symptoms
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={shareNotes}
                  onChange={(e) => setShareNotes(e.target.checked)}
                />
                Consultation notes
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sharePrescription}
                  onChange={(e) => setSharePrescription(e.target.checked)}
                />
                Prescription
              </label>
            </div>
          </div>
        )}

        {(formError || createError) && (
          <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {formError || createError}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:bg-teal-300"
          >
            {isCreating ? 'Adding…' : 'Add procedure'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddProcedureModal;
